import { Tab, Tabs, Grid, Tooltip, IconButton, Typography, Box, List, ListSubheader } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Link, useLocation, useParams, useNavigate, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetOneOrganizationProductQuery } from '../features/api/rest/apiProductOrganizationSlice';
import { useGetProfileQuery } from '../features/api/rest/apiProfileSlice';
import { useGetOrganizationUserQuery } from '../features/api/rest/apiOrganizationsSlice';
import ProductOrganizationBoard from './ProductOrganizationBoard';
import WordCloud from './WordCloud';
import ChartReview from './ChartReview';
import ProductOrganizationReviews from './ProductOrganizationReviews';
import Spinner from './Spinner';
import Error from './Error';
import LotsProduct from './Lots/LotsProduct';
import { useEffect } from 'react';
import Trends from './Trends';
import ProductOrganizationQuantitative from './ProductOrganizationQuantitative';

function ProductOrganizationDetails() {
  const { opk } = useParams();
  const { ean } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: product = [] } = useGetOneOrganizationProductQuery({ opk, ean });
  const backToProducts = () => {
    navigate(`/profile/organizations/${opk}/products`);
  };
  const { data: users = [], isFetching: isOrgFetching, isLoading: isOrgLoading } = useGetOrganizationUserQuery(opk, { refetchOnMountOrArgChange: true });
  const { data: profile, isFetching: isProfileFetching, isLoading: isProfileLoading, isError: isProfileError } = useGetProfileQuery();

  useEffect(() => {
    if (pathname === `/profile/organizations/${opk}/products/${ean}/` || pathname === `/profile/organizations/${opk}/products/${ean}`) {
      navigate(`/profile/organizations/${opk}/products/${ean}/charts/quantitative`);
    }
  }, [pathname, opk, ean]);

  const menuStyle = {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'top',
    justifyContent: 'left',
    gap: 1,
    backgroundColor: 'white',
    zIndex: 1,
    marginTop: '-30px',
    paddingTop: '70px',
    width: 'calc(100% + 30px)',
    ml: '-15px',
    pl: '15px',
  };

  const permission = () => {
    if (users.some((user) => user.is_admin && profile.id === user.user) || profile.is_staff) return true;
    return false;
  };

  if (isOrgFetching || isProfileFetching || isOrgLoading || isProfileLoading) return <Spinner />;

  if (isProfileError) return <Error />;

  return (
    <Box position="relative">
      <Typography variant="h4" py={0.5} noWrap sx={{ position: 'absolute', top: 0, zIndex: 3 }}>
        {product.name}
      </Typography>
      <List>
        <ListSubheader sx={{ p: 0, zIndex: 2 }}>
          <Grid container sx={menuStyle}>
            <Grid item>
              <Tooltip title={t('organizations.product.backToProducts')} placement="bottom">
                <IconButton onClick={backToProducts}>
                  <ArrowBackIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item flex={1} minWidth={0}>
              <Tabs value={pathname} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons={false} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {permission() && (
                  <Tab
                    label={t('organizations.product.charts.quantitative')}
                    component={Link}
                    to="charts/quantitative"
                    value={pathname.startsWith(`/profile/organizations/${opk}/products/${ean}/charts/quantitative`) ? pathname : `/profile/organizations/${opk}/products/${ean}`}
                  />
                )}
                {permission() && <Tab label={t('organizations.product.charts.qualitative')} component={Link} to="charts/wordcloud" value={`/profile/organizations/${opk}/products/${ean}/charts/wordcloud`} />}
                <Tab label={t('organizations.product.board.board')} component={Link} to="contents" value={`/profile/organizations/${opk}/products/${ean}/contents`} />
                {permission() && <Tab label={t('reviews.heading')} component={Link} to="reviews" value={`/profile/organizations/${opk}/products/${ean}/reviews`} />}
                {permission() && <Tab label={t('organizations.product.lots.lots')} component={Link} to="lots" value={`/profile/organizations/${opk}/products/${ean}/lots`} />}
              </Tabs>
            </Grid>
          </Grid>
        </ListSubheader>
        <Routes>
          <Route path="charts/quantitative/*" element={<ProductOrganizationQuantitative />} />
          <Route path="charts/wordcloud" element={<WordCloud />} />
          <Route path="contents" element={<ProductOrganizationBoard opk={opk} ean={ean} permission={permission} />} />
          <Route path="reviews" element={<ProductOrganizationReviews ean={ean} />} />
          <Route path="lots" element={<LotsProduct ean={ean} opk={opk} />} />
        </Routes>
      </List>
    </Box>
  );
}

export default ProductOrganizationDetails;
